// 📄 services/slackService.ts
//
// "Notificaciones más ricas" (2026-08-20, Fase 3) — un webhook de Slack
// POR ORGANIZACIÓN, configurado por su owner. Mismo patrón "cola en
// Firestore" que ya usa mailService.ts para email: el cliente NUNCA llama
// a Slack directo (evita CORS y no expone el webhook real en el
// navegador) — encola un doc en notificacionesSlack/{id}, y
// functions/triggers/slackNotify.js (Admin SDK, lee el webhook desde
// organizations/{orgId}/integraciones/slack) hace el POST de verdad.
import { doc, getDoc, setDoc, deleteField, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase';
import { getProject } from './projectService.ts';

export interface SlackConfig {
  webhookUrl: string;
}

const slackConfigRef = (orgId: string) => doc(db, 'organizations', orgId, 'integraciones', 'slack');

/** Solo el OWNER de la org puede leer esto (ver firestore.rules) — falla con permission-denied para cualquier otro. */
export const getSlackConfig = async (orgId: string): Promise<SlackConfig | null> => {
  const snap = await getDoc(slackConfigRef(orgId));
  return snap.exists() ? (snap.data() as SlackConfig) : null;
};

/** `webhookUrl: null` desconecta la integración (borra el campo). */
export const setSlackWebhook = async (orgId: string, webhookUrl: string | null): Promise<void> => {
  if (webhookUrl === null) {
    await setDoc(slackConfigRef(orgId), { webhookUrl: deleteField() }, { merge: true });
    return;
  }
  const trimmed = webhookUrl.trim();
  if (!trimmed.startsWith('https://hooks.slack.com/')) {
    throw new Error('La URL debe ser un webhook de Slack válido (empieza con https://hooks.slack.com/).');
  }
  await setDoc(slackConfigRef(orgId), { webhookUrl: trimmed }, { merge: true });
};

/**
 * Encola una notificación para la org — no falla ni avisa si esa org no
 * tiene Slack configurado (el trigger la descarta en silencio, mismo
 * criterio "no bloquear el flujo real" que ya usan las notificaciones por
 * email). Los 4 callers (invitación, incidente, caso de prueba fallido)
 * ya envuelven esto en `.catch(...)`, así que tampoco hace falta acá.
 */
export const notifySlackOrg = async (orgId: string, texto: string): Promise<void> => {
  if (!orgId) return;
  await addDoc(collection(db, 'notificacionesSlack'), { orgId, texto, createdAt: serverTimestamp() });
};

/**
 * Igual que notifySlackOrg, resolviendo el orgId a partir de un proyecto —
 * para eventos project-scoped (incidentes, casos de prueba) donde el
 * caller solo tiene projectId a mano, no orgId.
 */
export const notifySlackDeProyecto = async (projectId: string, texto: string): Promise<void> => {
  const project = await getProject(projectId);
  if (!project?.orgId) return;
  await notifySlackOrg(project.orgId, texto);
};
